import { AuthService } from './../../core/authentication/auth.service';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import Hotjar from '@hotjar/browser';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, firstValueFrom, merge, Subscription } from 'rxjs';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { AbsenceService } from 'src/app/data/absence.service';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AbsenceListDataSource } from './absence-list.datasource';
import { Filter } from 'src/app/models/common/filter';
import { WorkersTimesheetsListType } from 'src/app/workers/workers-timesheets/workers-timesheets.component';
import { SickLeaveModalComponent } from '../sick-leaves-modal/sick-leaves-modal.component';
import { Permission } from 'src/app/common/enums';
import moment from 'moment';
import { ScreenService } from 'src/app/core/screen.service';
import { AbsenceType } from 'src/app/models/enums/absence-type';
import { AbsenceApprovalPermissionDto, AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { AbsenceSyncStatusEnum } from 'src/app/models/enums/absence-sync-status-enum';
import { AbsenceLimitCalcTypeEnum } from 'src/app/models/enums/absence-limit-calc-type-enum';
import { DatePipe } from '@angular/common';

const DEFAULT_NAME_SORT_FIELD = 'fullName';
const ALTERNATIVE_NAME_SORT_FIELD = 'lastName';

@Component({
  selector: 'app-absence-list',
  templateUrl: './absence-list.component.html',
  styleUrls: ['./absence-list.component.scss'],
  providers: [AbsenceListDataSource, DatePipe],
  standalone: false
})
export class AbsenceListComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  screenService = inject(ScreenService);
  private absenceService = inject(AbsenceService);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private snackbar = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private authService = inject(AuthService);
  private datePipe = inject(DatePipe);
  dataSource = inject(AbsenceListDataSource, { self: true });

  displayedColumns = [
    'lastName',
    'company',
    'employer',
    'employerObject',
    'absenceType',
    'sentToApprovalOn',
    'startDate',
    'businessDays',
    'calendarDays',
    'absenceReason',
    'status',
  ];

  @Input() filters: Filter[];
  @Input() listType: WorkersTimesheetsListType;
  @Input() defaultSort: Sort;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private subscriptions: Subscription[] = [];


  constructor() {
    if (this.isSentToExternamSystemVisible()) {
      this.displayedColumns.push('isSentToExteralSystem');
      this.displayedColumns.push('actions');
    } else {
      this.displayedColumns.push('actions');
    }
  }

  get AbsenceSyncStatusEnum() {
    return AbsenceSyncStatusEnum;
  }

  get AbsenceLimitCalcTypeEnum() {
    return AbsenceLimitCalcTypeEnum;
  }

  ngOnInit(): void {
    Hotjar.event('lista nieobecności!');
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0)));

    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange).subscribe(() => this.fetchAbsences()),
    );

    this.subscriptions.push(
      this.dataSource.loading$.subscribe({
        next: (isLoading) => (isLoading ? this.spinner.show() : this.spinner.hide()),
        complete: () => this.spinner.hide(),
      }),
    );

    this.fetchAbsences(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.dataSource) return;

    if (!!changes.defaultSort && !changes.defaultSort.firstChange) {
      const defaultSort = changes.defaultSort.currentValue as Sort;

      if (defaultSort.active != this.sort.active || defaultSort.direction != this.sort.direction) {
        this.sort.sort({ id: defaultSort.active, start: defaultSort.direction } as MatSortable);
      }
    }

    if (this.paginator) {
      this.fetchAbsences(true);
    }
  }

  hasAnyAction(absence: AbsenceDto): boolean {
    return (
      !!absence.SickLeaveId ||
      this.isSendToApprovalButtonVisible(absence.StatusId, absence.ApprovalRequired) ||
      this.isConfirmAbsenceButtonVisible(absence.StatusId, absence.ApprovalRequired) ||
      this.isDeleteButtonVisible(absence.StatusId) ||
      this.isBackToDraftButtonVisible(absence.StatusId) ||
      this.isAcceptAbsenceButtonVisible(absence.StatusId, absence.AuthServerUserId) ||
      this.isRejectAbsenceButtonVisible(absence.StatusId, absence.AuthServerUserId) ||
      this.isCancelAbsenceButtonVisible(absence.StatusId, absence.AbsenceTypeId)
    );
  }

  isSentToExternamSystemVisible = () => this.authService.hasPermission(Permission.ShowIsSentToExteralSystemFilter);

  isSendToApprovalButtonVisible = (absenceStatusId: number, isApprovalRequired: boolean): boolean =>
    isApprovalRequired && absenceStatusId === AbsenceStatus.Draft;

  sendToApproval(absenceId: number): void {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.sendToApproval(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullySentToApprovalAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSendingToApprovalAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  isConfirmAbsenceButtonVisible = (absenceStatusId: number, isApprovalRequired: boolean): boolean =>
    !isApprovalRequired && absenceStatusId === AbsenceStatus.Draft;

  confirmAbsence(absenceId: number) {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.confirmAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyConfirmedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmConfirmingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  isDeleteButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.Draft;

  deleteAbsence(absenceId: number) {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.deleteAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyDeletedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmDeletingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  isBackToDraftButtonVisible = (absenceStatusId: number) => absenceStatusId === AbsenceStatus.PendingApproval;

  backToDraftAbsence(absenceId: number) {
    const onConfirm = (absenceId) => {
      this.spinner.show();
      return this.absenceService.backToDraftAbsence(absenceId).pipe(
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyBackToDraftAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmBackToDraftAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(absenceId) : EMPTY)),
      )
      .subscribe();
  }

  isAcceptAbsenceButtonVisible = (absenceStatusId: number, authServerUserId: string) =>
    absenceStatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId !== authServerUserId;

  acceptAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.acceptAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyAcceptedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmAcceptingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
      )
      .subscribe();
  }

  isRejectAbsenceButtonVisible = (absenceStatusId: number, authServerUserId: string) =>
    absenceStatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId !== authServerUserId;

  rejectAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.rejectAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccesfullyRejectedAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmRejectingAbsenceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
      )
      .subscribe();
  }

  isCancelAbsenceButtonVisible = (absenceStatusId: number, absenceTypeId: number): boolean =>
    absenceStatusId === AbsenceStatus.Accepted && absenceTypeId !== AbsenceType.SickLeave;

  async cancelAbsence(absenceId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.absenceService.cancelAbsence(absenceId).pipe(
        first(),
        tap((_) => this.fetchAbsences()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyCanceledAbsence)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmCancellingAbsenceMessage } })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  openDetailsModal(sickLeaveId: number) {
    this.dialog
      .open(SickLeaveModalComponent, {
        data: { SickLeaveId: sickLeaveId },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.fetchAbsences();
        }
      });
  }

  displayDatesWithDecisions(element: AbsenceDto): string {
    const formatDate = (date: Date | null) => (date ? moment(date).format('YYYY-MM-DD') : '');

    let tooltip = '';

    if (element.SentToApprovalOn) {
      tooltip = this.translateService.instant('AbsenceTooltip.SubmissionDate', {
        date: formatDate(element.SentToApprovalOn),
      });
    }

    if (element.StatusId === AbsenceStatus.Accepted && element.AcceptedOn) {
      const acceptedText = this.translateService.instant('absences.AcceptedOn') + ': ' + formatDate(element.AcceptedOn);
      tooltip = tooltip ? tooltip + '\n' + acceptedText : acceptedText;
    }

    if (element.StatusId === AbsenceStatus.Rejected && element.RejectedOn) {
      const rejectedText = this.translateService.instant('absences.RejectedOn') + ': ' + formatDate(element.RejectedOn);
      tooltip = tooltip ? tooltip + '\n' + rejectedText : rejectedText;
    }

    if (element.StatusId === AbsenceStatus.PendingApproval && element.LastDecisiveApprovalDate && element.LastDecisiveApprovalUserName) {
      const decisionKey = element.LastDecisiveApprovalIsPositive ? 'AbsenceTooltip.AcceptedBy' : 'AbsenceTooltip.RejectedBy';

      const decisionText = this.translateService.instant(decisionKey, {
        userName: element.LastDecisiveApprovalUserName,
        date: formatDate(element.LastDecisiveApprovalDate),
      });

      if (tooltip) {
        tooltip += '\n' + decisionText;
      } else {
        tooltip = decisionText;
      }
    }

    return tooltip;
  }

  shouldShowStatusTooltip(element: AbsenceDto) {
    return element.StatusId === AbsenceStatus.Accepted || element.StatusId === AbsenceStatus.Rejected;
  }

  shouldShowProgressSpinner(element: AbsenceDto): boolean {
    return element.StatusId === AbsenceStatus.PendingApproval && element.TotalApprovalSteps !== null && element.TotalApprovalSteps > 1;
  }

  getStatusTooltip(element: AbsenceDto): string {
    if (element.IsImported) {
      return this.translateService.instant('AbsenceStatusTooltip.Imported');
    }

    if (!element.ApprovalRequired) {
      return this.translateService.instant('AbsenceStatusTooltip.NoApprovalRequired');
    }

    if (element.StatusId === AbsenceStatus.Accepted) {
      return this.translateService.instant('AbsenceStatusTooltip.HistoryInformation', {
        status: element.Status?.toLowerCase(),
        changedBy: element.AcceptedBy,
        changedDate: this.datePipe.transform(element.AcceptedOn, 'yyyy-MM-dd HH:mm'),
      });
    }

    if (element.StatusId === AbsenceStatus.Rejected) {
      return this.translateService.instant('AbsenceStatusTooltip.HistoryInformation', {
        status: element.Status?.toLowerCase(),
        changedBy: element.RejectedBy,
        changedDate: this.datePipe.transform(element.RejectedOn, 'yyyy-MM-dd HH:mm'),
      });
    }

    return '';
  }

  private fetchAbsences(resetPage: boolean = false) {
    if (resetPage) {
      this.paginator.pageIndex = 0;
    }

    this.dataSource.fetchAbsences(
      this.paginator.pageIndex + 1,
      this.paginator.pageSize,
      this.sort.active !== DEFAULT_NAME_SORT_FIELD ? this.sort.active : ALTERNATIVE_NAME_SORT_FIELD,
      this.sort.direction,
      this.filters,
    );
  }

  approvingButtonsVisible(element: AbsenceDto) {
    return element.ApprovalPermissions?.some((permissionDto) => this.authService.hasPermission(permissionDto.Permission as Permission)) || false;
  }
}
